/**
 * useIcd10UsageSync — Feb 28, 2026
 *
 * Listens for the `writeright:icd10-usage-flush` window event and POSTs
 * the local ICD-10 chip-tap map to /api/me/icd10-usage on a 1.5s debounce.
 * Server merge semantics (max per code) means concurrent device taps
 * never lose counts.
 *
 * Also flushes on tab visibility-hidden so an unexpectedly closed tab
 * doesn't lose taps. And flushes on first mount AFTER user is loaded so
 * we catch any taps that happened while offline.
 *
 * Reset path: the Modals.jsx "Reset Personal Chip Order" button writes
 * `{}` to localStorage and dispatches `writeright:icd10-usage-flush` AND
 * `writeright:icd10-usage-reset` so this hook fires a REPLACE write.
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';

const API = window.__WR_API_BASE__;
const STORAGE_KEY = 'writeright_icd10_usage_v1';
const DEBOUNCE_MS = 1500;

const readLocal = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
};

export const useIcd10UsageSync = (user) => {
  const timerRef = useRef(null);
  const lastSentRef = useRef('');
  const lastResetRef = useRef(false);
  const userIdRef = useRef(null);

  useEffect(() => {
    userIdRef.current = user?.user_id || null;
  }, [user]);

  useEffect(() => {
    if (!API) return undefined;

    const flush = async (force = false) => {
      if (!userIdRef.current) return; // guest mode — no server write
      const usage = readLocal();
      const wasReset = lastResetRef.current;
      lastResetRef.current = false;
      const payload = wasReset
        ? { usage, replace: true }
        : { usage };
      const sig = JSON.stringify(payload);
      if (!force && sig === lastSentRef.current) return;
      lastSentRef.current = sig;
      try {
        await axios.post(`${API}/api/me/icd10-usage`, payload, {
          withCredentials: true,
        });
      } catch (err) {
        // Telemetry-style: never toast on failure. Reset lastSentRef so
        // the next attempt retries the same payload.
        if (err?.response?.status >= 500) {
          console.warn('ICD-10 usage flush failed:', err.message);
        }
        lastSentRef.current = '';
      }
    };

    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => flush(false), DEBOUNCE_MS);
    };

    const onReset = () => {
      lastResetRef.current = true;
      schedule();
    };
    const onFlush = () => schedule();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        flush(true);
      }
    };

    window.addEventListener('writeright:icd10-usage-flush', onFlush);
    window.addEventListener('writeright:icd10-usage-reset', onReset);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Initial post-login flush so any taps that happened before we knew
    // about this user (e.g., guest -> sign-in) get captured.
    if (userIdRef.current) schedule();

    return () => {
      window.removeEventListener('writeright:icd10-usage-flush', onFlush);
      window.removeEventListener('writeright:icd10-usage-reset', onReset);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user?.user_id]);
};

export default useIcd10UsageSync;
